import { clientLogger } from '@/lib/clientLogger';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobDTO, JobMergePatchDTO } from '@/types';
import { Banknote, Calculator, ChevronDown, Edit3, HelpCircle } from 'lucide-react';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { TechnicianCutDetailsModal } from './TechnicianCutDetailsModal';
import { JobProfitDetailsModal } from './JobProfitDetailsModal';
import { TechnicianPayOverrideModal } from './TechnicianPayOverrideModal';
import { EditableField } from './ui/EditableField';
import { showAlert } from '@/lib/capacitor';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { CollapsibleSummaryHeader } from './ui/CollapsibleSummaryHeader';
import { useSettledJobGuard } from '@/hooks/useSettledJobGuard';
import { JobBalanceSettle } from './JobBalanceSettle';

interface TechnicianPaySectionProps {
  job: JobDTO;
  onJobUpdated?: (updatedJob: JobDTO) => void;
  compact?: boolean;
}

export function TechnicianPaySection({ job, onJobUpdated, compact = false }: TechnicianPaySectionProps) {
  const { canViewPayoutFields } = useJobEditPermissions();
  const { userRole } = useCurrentUser();
  const isTechnician = userRole === 'technician';

  const [isExpanded, setIsExpanded] = useState(false);

  const [showTechCutModal, setShowTechCutModal] = useState(false);
  const [showProfitModal, setShowProfitModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const queryClient = useQueryClient();

  // Guard balance-affecting changes to a settled job (req 11).
  const { guard, guardDialog } = useSettledJobGuard(job, onJobUpdated);

  // Check if technician has a compensation plan for this company (not needed for technician role)
  const { data: compensationPlans } = useQuery({
    queryKey: ['compensationPlans', job.assigned_technician?.id],
    queryFn: () => apiService.getTechnicianCompensationPlans(job.assigned_technician!.id),
    enabled: !!job.assigned_technician && !isTechnician,
  });

  // Check if there's a plan for this specific company
  const hasCompensationPlan = compensationPlans?.some(
    plan => plan.company_id === job.company?.id
  ) ?? false;

  // Update job mutation for source cost
  const updateJobMutation = useMutation({
    mutationFn: (data: JobMergePatchDTO) => apiService.mergePatchJob(job.id, data),
    onSuccess: (updatedJob: JobDTO) => {
      // Update the single job query
      queryClient.setQueryData(['job', job.id], updatedJob);
      // Update the jobs list query
      queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
        if (!existing) return existing;
        const isArray = Array.isArray(existing);
        const items = isArray ? existing : existing.jobs;
        if (!Array.isArray(items)) return existing;
        const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
        if (isArray) return updatedItems;
        return { ...existing, jobs: updatedItems };
      });
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }
    },
    onError: (error: any) => {
      clientLogger.error('Error updating job:', error);
    },
  });

  const handleSourceCommissionSave = async (value: any) => {
    const cleanValue = value === '' ? null : value;
    await updateJobMutation.mutateAsync({
      source_commission: cleanValue
    });
  };

  // Calculate compensation mutation
  const calculateMutation = useMutation({
    // Refetch after calculating: the response carries the cut but not the recalculated profit
    mutationFn: async () => {
      await apiService.calculateTechnicianCut(job.id);
      return apiService.getJobById(job.id);
    },
    onSuccess: (updatedJob: JobDTO) => {
      queryClient.setQueryData(['job', job.id], updatedJob);
      queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
        if (!existing) return existing;
        const isArray = Array.isArray(existing);
        const items = isArray ? existing : existing.jobs;
        if (!Array.isArray(items)) return existing;
        const updatedItems = items.map((j: JobDTO) => (j.id === updatedJob.id ? updatedJob : j));
        return isArray ? updatedItems : { ...existing, jobs: updatedItems };
      });
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }
    },
    onError: (error: any) => {
      clientLogger.error('Failed to calculate compensation:', error);
      // Extract error message from API response
      const errorMessage = error?.response?.data?.details ||
        error?.response?.data?.title ||
        error?.message ||
        'Failed to calculate technician compensation. Please try again.';
      showAlert(errorMessage, 'Calculation Error');
    }
  });

  const handleCalculate = async () => {
    guard(async () => { await calculateMutation.mutateAsync(); });
  };

  const handleOpenOverride = () => {
    guard(() => setShowOverrideModal(true));
  };

  const handleOverrideSuccess = async () => {
    setShowOverrideModal(false);
    // Refresh job data
    try {
      const updatedJob = await apiService.getJobById(job.id);
      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    } catch (error) {
      clientLogger.error('Failed to refresh job after override:', error);
    }
  };

  const technicianCut = job.technician_cut ?? 0;
  const jobProfit = job.job_profit;
  const details = job.technician_cut_details;
  const profitDetails = job.job_profit_details;
  const hasCalculation = technicianCut > 0 || details !== null;
  const isOverride = details?.is_override ?? false;
  const hasAssignedTechnician = !!job.assigned_technician;
  const isSettled = job.balance_settled_at != null;
  const settleBadge = (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isSettled ? '' : 'bg-red-100 text-red-700'}`}
      style={isSettled ? { backgroundColor: '#16a34a', color: '#ffffff' } : undefined}
    >
      {isSettled ? 'Settled' : 'Unsettled'}
    </span>
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Hide entire section if user lacks view.payout permission (technicians always see their own payout)
  if (!isTechnician && !canViewPayoutFields) return null;

  return (
    <>
      <div className={compact
        ? 'bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'
        : `bg-gray-50 rounded-lg ${isExpanded ? 'p-4' : 'p-3'} transition-all duration-200`
      }>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={compact
            ? 'w-full px-2.5 py-2.5 flex items-center justify-between gap-2 bg-gray-50/50 hover:bg-gray-100/50 transition-colors'
            : 'w-full flex items-center justify-between'
          }
        >
          {compact ? (
            <CollapsibleSummaryHeader
              leading={
                <div className="flex items-center gap-1.5 shrink-0">
                  <Banknote className="h-4 w-4 text-violet-500 shrink-0" />
                  <span className="font-semibold text-gray-900">Payout</span>
                </div>
              }
              trailing={
                !isExpanded ? (
                  <>
                    {(hasAssignedTechnician || jobProfit !== null) && (
                      <>
                        <span className="text-gray-600 font-semibold whitespace-nowrap">Tech Cut: <span className={`font-bold ${hasCalculation ? 'text-green-600' : 'text-gray-400'}`}>{formatCurrency(technicianCut)}</span></span>
                        {!isTechnician && jobProfit !== null && jobProfit !== undefined && (
                          <>
                            <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                            <span className="text-gray-600 font-semibold whitespace-nowrap">Profit: <span className={`font-bold ${jobProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(jobProfit)}</span></span>
                          </>
                        )}
                        <div className="w-px h-4 bg-gray-300 shrink-0" aria-hidden />
                      </>
                    )}
                    {settleBadge}
                  </>
                ) : undefined
              }
            />
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Banknote className="h-5 w-5 text-violet-500" />
                <span className="font-semibold text-gray-900 text-base">Payout</span>
              </div>

              {/* Collapsed Summary */}
              {!isExpanded && (
                <div className="flex items-center gap-4 text-sm hidden sm:flex">
                  {(hasAssignedTechnician || jobProfit !== null) && (
                    <>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <span className="text-gray-600 font-semibold">Tech Cut: <span className={`font-bold ${hasCalculation ? 'text-green-600' : 'text-gray-400'}`}>{formatCurrency(technicianCut)}</span></span>
                      {!isTechnician && jobProfit !== null && jobProfit !== undefined && (
                        <>
                          <div className="w-px h-4 bg-gray-300"></div>
                          <span className="text-gray-600 font-semibold">Profit: <span className={`font-bold ${jobProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(jobProfit)}</span></span>
                        </>
                      )}
                    </>
                  )}
                  <div className="w-px h-4 bg-gray-300"></div>
                  {settleBadge}
                </div>
              )}
            </div>
          )}

          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {isExpanded && (
          <div className={compact ? 'p-3 space-y-2 border-t border-gray-100 animate-in slide-in-from-top-1' : 'space-y-3 pt-2'}>
            {/* No technician assigned warning - admin only */}
            {!isTechnician && !hasAssignedTechnician && (
              <div className={`bg-yellow-50 border border-yellow-200 rounded-md ${compact ? 'p-2' : 'p-3'}`}>
                <p className={`${compact ? 'text-xs' : 'text-sm'} text-yellow-800`}>
                  No technician assigned to this job. Assign a technician to calculate compensation.
                </p>
              </div>
            )}

            {/* No compensation plan warning - admin only */}
            {!isTechnician && hasAssignedTechnician && !hasCompensationPlan && (
              <div className={`bg-amber-50 border border-amber-200 rounded-md ${compact ? 'p-2' : 'p-3'}`}>
                <p className={`${compact ? 'text-xs' : 'text-sm'} text-amber-800`}>
                  No compensation plan exists for this technician. Create a plan or use the manual override option.
                </p>
              </div>
            )}

            {/* Payout Rate - editable for admin/secretary, read-only for technician */}
            <div className="flex items-center justify-between">
              <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>Payout Rate:</span>
              {isTechnician ? (
                <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-900`}>{job.rate_type || 'Normal'}</span>
              ) : (
                <EditableField
                  value={job.rate_type || 'Normal'}
                  type="select"
                  options={[
                    { value: 'Normal', label: 'Normal' },
                    { value: 'Night', label: 'Night' },
                    { value: 'Special', label: 'Special' },
                  ]}
                  onSave={async (val) => { await updateJobMutation.mutateAsync({ rate_type: val as 'Normal' | 'Night' | 'Special' }); }}
                  inline={true}
                  alwaysShowEdit={true}
                  renderValue={(val) => (
                    <span className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-gray-900`}>{val as string || 'Normal'}</span>
                  )}
                />
              )}
            </div>

            {/* Source Cost - admin only */}
            {!isTechnician && (
              <div className="flex items-center justify-between">
                <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>Source Cost:</span>
                <div className="flex items-center gap-2">
                  <EditableField
                    value={job.source_commission}
                    type="number"
                    placeholder="0.00"
                    onSave={handleSourceCommissionSave}
                    inline={true}
                    alwaysShowEdit={compact}
                    renderValue={(val) => (
                      <span className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${val ? 'text-gray-900' : 'text-gray-400'}`}>
                        {val ? formatCurrency(val) : '$0.00'}
                      </span>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Technician Cut Display */}
            <div className="flex items-center justify-between">
              <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>Technician Cut:</span>
              <div className="flex items-center gap-2">
                <span className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${hasCalculation ? 'text-green-600' : 'text-gray-400'}`}>
                  {formatCurrency(technicianCut)}
                </span>
                {isOverride && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                    Override
                  </span>
                )}
                {/* Question mark icon for tech cut details */}
                {hasCalculation && details && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTechCutModal(true);
                    }}
                    className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                    title="View details"
                  >
                    <HelpCircle className="h-4 w-4 text-blue-500" />
                  </button>
                )}
              </div>
            </div>

            {/* Technician Balance Display */}
            {details?.technician_balance !== undefined && details?.technician_balance !== null && (
              <div className="flex items-center justify-between">
                <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>
                  {details.technician_balance >= 0 ? 'Company Owes Tech:' : 'Tech Owes Company:'}
                </span>
                <div className="flex items-center gap-2">
                  <span className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${details.technician_balance > 0 ? 'text-amber-500' : details.technician_balance < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                    {formatCurrency(Math.abs(details.technician_balance))}
                  </span>
                  {/* Question mark icon for tech cut details */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTechCutModal(true);
                    }}
                    className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                    title="View details"
                  >
                    <HelpCircle className="h-4 w-4 text-blue-500" />
                  </button>
                </div>
              </div>
            )}

            <JobBalanceSettle job={job} onJobUpdated={onJobUpdated} compact />

            {/* Job Profit Display - admin only */}
            {!isTechnician && jobProfit !== null && jobProfit !== undefined && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className={`${compact ? 'text-sm' : 'text-sm'} font-medium text-gray-700`}>Job Profit:</span>
                <div className="flex items-center gap-2">
                  <span className={`${compact ? 'text-sm' : 'text-lg'} font-bold ${jobProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(jobProfit)}
                  </span>
                  {/* Question mark icon for profit details */}
                  {profitDetails && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowProfitModal(true);
                      }}
                      className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                      title="View details"
                    >
                      <HelpCircle className="h-4 w-4 text-blue-500" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons - admin only */}
            {!isTechnician && <div className="flex flex-wrap gap-2 pt-2">
              {hasAssignedTechnician && (
                <>
                  {/* Shown without a plan too: the cut is $0 but the profit still gets calculated */}
                  <button
                    type="button"
                    onClick={handleCalculate}
                    disabled={calculateMutation.isPending}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {calculateMutation.isPending ? (
                      <LoadingSpinner />
                    ) : (
                      <Calculator className="h-4 w-4" />
                    )}
                    {hasCalculation || jobProfit != null ? 'Recalculate' : 'Calculate'}
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenOverride}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                    Override
                  </button>
                </>
              )}
            </div>}
          </div>
        )}
      </div>

      {/* Technician Cut Details Modal */}
      <TechnicianCutDetailsModal
        isOpen={showTechCutModal}
        onClose={() => setShowTechCutModal(false)}
        details={details ?? undefined}
        technicianName={job.assigned_technician?.name}
      />

      {/* Job Profit Details Modal */}
      <JobProfitDetailsModal
        isOpen={showProfitModal}
        onClose={() => setShowProfitModal(false)}
        profitDetails={profitDetails ?? undefined}
      />

      {/* Override Modal */}
      <TechnicianPayOverrideModal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        jobId={job.id}
        currentAmount={technicianCut}
        onSuccess={handleOverrideSuccess}
      />

      {/* Settled-job change guard (req 11) */}
      {guardDialog}
    </>
  );
}
